'use client';
import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useLanguage } from '@/lib/LanguageContext';
import { getTranslations } from '@/lib/i18n';

interface PortfolioItem {
  id: number;
  titleEs: string;
  titleFr: string;
  categoryEs: string;
  categoryFr: string;
  image: string;
  alt: string;
  href: string;
}

const portfolioItems: PortfolioItem[] = [
{
  id: 1,
  titleEs: 'Infraestructura TI',
  titleFr: 'Infrastructure IT',
  categoryEs: 'Redes y Servidores',
  categoryFr: 'Réseaux et Serveurs',
  image: 'https://innobox.cl/wp-content/uploads/2021/07/2-940x529.png',
  alt: 'Infraestructura TI - redes y servidores empresariales Innobox',
  href: '/portfolio/infraestructura'
},
{
  id: 2,
  titleEs: 'Ciberseguridad',
  titleFr: 'Cybersécurité',
  categoryEs: 'Protección de Datos',
  categoryFr: 'Protection des Données',
  image: 'https://innobox.cl/wp-content/uploads/2021/07/1-940x529.png',
  alt: 'Ciberseguridad - protección de datos y sistemas Innobox',
  href: '/portfolio/ciberseguridad'
},
{
  id: 3,
  titleEs: 'Asesoría y Consultoría TI',
  titleFr: 'Conseil et Consulting IT',
  categoryEs: 'Estrategia Digital',
  categoryFr: 'Stratégie Numérique',
  image: 'https://innobox.cl/wp-content/uploads/2021/07/3-940x529.png',
  alt: 'Asesoría y Consultoría TI - planificación estratégica digital Innobox',
  href: '/portfolio/asesoria-consultoria-ti'
},
{
  id: 4,
  titleEs: 'Desarrollo de Software',
  titleFr: 'Développement Logiciel',
  categoryEs: 'Aplicaciones a Medida',
  categoryFr: 'Applications Sur Mesure',
  image: 'https://innobox.cl/wp-content/uploads/2021/07/4-940x529.png',
  alt: 'Desarrollo de Software - aplicaciones a medida Innobox',
  href: '/portfolio/desarrollo-de-software'
}];


const PortfolioSection: React.FC = () => {
  const { language } = useLanguage();
  const t = getTranslations(language);
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const sectionTitle = language === 'fr' ? 'Portfolio' : 'Portfolio';
  const sectionSubtitle = language === 'fr' ? 'Nos Projets Réalisés' : 'Proyectos Realizados';
  const verProyecto = language === 'fr' ? 'Voir le projet' : 'Ver proyecto';
  const masInfo = language === 'fr' ? 'Plus d\'informations' : 'Más información';

  return (
    <section
      id="portfolio"
      ref={sectionRef}
      className="relative overflow-hidden"
      style={{ backgroundColor: '#202427', paddingTop: '120px', paddingBottom: '120px' }}>
      
      {/* Grid lines */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 bottom-0 left-[25%] w-px bg-white/5" />
        <div className="absolute top-0 bottom-0 left-[50%] w-px bg-white/5" />
        <div className="absolute top-0 bottom-0 left-[75%] w-px bg-white/5" />
        <div className="absolute left-0 right-0 top-0 h-px bg-white/6" />
        <div className="absolute left-0 right-0 bottom-0 h-px bg-white/6" />
      </div>

      <div className="max-w-[1280px] mx-auto px-6 relative z-10">
        {/* Header */}
        <div
          className={`flex flex-col sm:flex-row sm:items-end sm:justify-between mb-12 gap-6 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`
          }>
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-6 h-px bg-[#abd233]" />
              <span style={{ fontSize: '12px', letterSpacing: '0.1em', color: '#abd233', textTransform: 'uppercase' }}>
                {sectionTitle}
              </span>
            </div>
            <h2
              className="text-white"
              style={{
                fontSize: 'clamp(28px, 3vw, 48px)',
                fontWeight: 400,
                lineHeight: 1.05,
                letterSpacing: '-0.01em'
              }}>
              {sectionSubtitle}
            </h2>
          </div>
          <a
            href="#contacto"
            className="group relative inline-flex items-center overflow-hidden flex-shrink-0"
            style={{
              border: '1px solid rgba(255,255,255,0.2)',
              padding: '11px 22px',
              fontSize: '13px',
              color: '#ffffff',
              letterSpacing: '0.02em',
              transition: 'all 300ms ease-out'
            }}>
            <span
              className="absolute inset-0 bg-white transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300"
              style={{ zIndex: 0 }} />
            
            <span className="relative z-10 transition-colors duration-300 group-hover:text-[#141618]">
              {masInfo}
            </span>
          </a>
        </div>

        {/* Portfolio grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
          {portfolioItems.map((item, index) => {
            const title = language === 'fr' ? item.titleFr : item.titleEs;
            const category = language === 'fr' ? item.categoryFr : item.categoryEs;
            return (
              <article
                key={item.id}
                className="group relative overflow-hidden border border-white/6 hover:border-[#abd233]/30 transition-all duration-300"
                style={{
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                  transition: `opacity 0.6s ease ${index * 100}ms, transform 0.6s ease ${index * 100}ms, border-color 0.3s`
                }}>
                <Link href={item.href} className="block">
                  {/* Image */}
                  <div className="overflow-hidden" style={{ aspectRatio: '16/9' }}>
                    <img
                      src={item.image}
                      alt={item.alt}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    
                    {/* Overlay */}
                    <div
                      className="absolute inset-0 transition-opacity duration-300"
                      style={{ background: 'linear-gradient(to top, rgba(20,22,24,0.9) 0%, rgba(20,22,24,0.2) 60%, transparent 100%)' }} />
                    
                    {/* Diagonal line */}
                    <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <svg className="w-full h-full" preserveAspectRatio="none" viewBox="0 0 100 100">
                        <line x1="0" y1="100" x2="100" y2="0" stroke="rgba(171,210,51,0.3)" strokeWidth="0.5" vectorEffect="non-scaling-stroke" />
                      </svg>
                    </div>
                  </div>

                  {/* Info bar */}
                  <div className="p-6 flex items-center justify-between" style={{ backgroundColor: '#1a1d20' }}>
                    <div>
                      <div className="text-white/40 mb-1" style={{ fontSize: '11px', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                        {category}
                      </div>
                      <h3
                        className="text-white group-hover:text-[#abd233] transition-colors duration-300"
                        style={{ fontSize: '18px', fontWeight: 400, letterSpacing: '-0.01em' }}>
                        {title}
                      </h3>
                    </div>
                    <div className="flex items-center gap-2 text-[#abd233] opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex-shrink-0">
                      <span style={{ fontSize: '12px', letterSpacing: '0.05em' }}>{verProyecto}</span>
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M1 6h10M7 2l4 4-4 4" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                  </div>
                </Link>
              </article>);

          })}
        </div>
      </div>
    </section>);

};

export default PortfolioSection;